import { useState, useEffect, useRef, useCallback } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Slider } from "@/components/ui/slider";
import { CameraIcon, Loader2Icon } from "lucide-react";
import Cropper, { Area } from "react-easy-crop";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import Header from "@/components/Header";
import { useLanguage } from "@/contexts/LanguageContext";
import { useToast } from "@/hooks/use-toast";
import { getApiEndpoint } from "@/lib/api";
import { getToken, getUser, updateUser } from "@/lib/auth-storage";

const createImage = (url: string): Promise<HTMLImageElement> =>
  new Promise((resolve, reject) => {
    const img = new Image();
    img.addEventListener("load", () => resolve(img));
    img.addEventListener("error", (e) => reject(e));
    img.src = url;
  });

async function getCroppedImg(imageSrc: string, pixelCrop: Area): Promise<Blob> {
  const image = await createImage(imageSrc);
  const canvas = document.createElement("canvas");
  canvas.width = pixelCrop.width;
  canvas.height = pixelCrop.height;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("No 2d context");
  ctx.drawImage(
    image,
    pixelCrop.x,
    pixelCrop.y,
    pixelCrop.width,
    pixelCrop.height,
    0,
    0,
    pixelCrop.width,
    pixelCrop.height
  );
  return new Promise((resolve, reject) => {
    canvas.toBlob((blob) => (blob ? resolve(blob) : reject(new Error("Canvas toBlob failed"))), "image/jpeg", 0.92);
  });
}

interface ProfileUser {
  id: number;
  email: string;
  name: string;
  phone?: string;
  role: string;
  company_id?: number;
  avatar_url: string | null;
}

const EditProfile = () => {
  const { t } = useLanguage();
  const { toast } = useToast();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [profile, setProfile] = useState<ProfileUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [name, setName] = useState("");
  const [savingName, setSavingName] = useState(false);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const [codeSent, setCodeSent] = useState(false);
  const [code, setCode] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [sendingCode, setSendingCode] = useState(false);
  const [changingPassword, setChangingPassword] = useState(false);
  const [cropImageSrc, setCropImageSrc] = useState<string | null>(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null);
  
  // Estados para configuração do Jira
  const [jiraLoading, setJiraLoading] = useState(true);
  const [jiraSaving, setJiraSaving] = useState(false);
  const [jiraTesting, setJiraTesting] = useState(false);
  const [jiraConfig, setJiraConfig] = useState({
    enabled: false,
    api_url: "",
    email: "",
    api_token: "",
    project_key: "",
    board_id: null as number | null,
    issue_type: "Task"
  });
  const [jiraProjects, setJiraProjects] = useState<Array<{ key: string; name: string; id: string }>>([]);
  const [jiraBoards, setJiraBoards] = useState<Array<{ id: number; name: string; type: string }>>([]);
  const [loadingJiraProjects, setLoadingJiraProjects] = useState(false);
  const [loadingJiraBoards, setLoadingJiraBoards] = useState(false);
  const [jiraOAuthStarting, setJiraOAuthStarting] = useState(false);

  const token = getToken();

  const ATLASSIAN_TOKEN_URL = "https://id.atlassian.com/manage-profile/security/api-tokens";

  const onCropComplete = useCallback((_croppedArea: Area, croppedAreaPx: Area) => {
    setCroppedAreaPixels(croppedAreaPx);
  }, []);

  const fetchProfile = async () => {
    if (!token) return;
    try {
      const res = await fetch(getApiEndpoint("/api/profile"), {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error("Erro ao carregar perfil");
      const data = await res.json();
      setProfile(data.user);
      setName(data.user.name || "");
    } catch (e) {
      toast({ title: "Erro", description: "Não foi possível carregar o perfil.", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProfile();
    fetchJiraConfig();
  }, [token]);
  
  const fetchJiraConfig = async () => {
    if (!token) return;
    try {
      setJiraLoading(true);
      // Buscar configuração pessoal do usuário (não da empresa)
      const response = await fetch(getApiEndpoint("/api/jira/config?config_type=user"), {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        if (data.config) {
          // Só mostrar se for configuração do usuário (não da empresa)
          if (data.config.user_id && !data.config.company_id) {
            setJiraConfig({
              enabled: data.config.enabled || false,
              api_url: data.config.api_url || "",
              email: data.config.email || "",
              api_token: data.config.api_token || "",
              project_key: data.config.project_key || "",
              board_id: data.config.board_id || null,
              issue_type: data.config.issue_type || "Task"
            });
          } else {
            // Se vier config da empresa, limpar (usuário não tem config pessoal)
            setJiraConfig({
              enabled: false,
              api_url: "",
              email: "",
              api_token: "",
              project_key: "",
              board_id: null,
              issue_type: "Task"
            });
          }
        }
      }
    } catch (error) {
      console.error("Erro ao buscar configuração do Jira:", error);
    } finally {
      setJiraLoading(false);
    }
  };

  const handleJiraTestConnection = async () => {
    if (!jiraConfig.api_url || !jiraConfig.email || !jiraConfig.api_token) {
      toast({
        title: "Campos obrigatórios",
        description: "Preencha URL, Email e Token da API antes de testar",
        variant: "destructive",
      });
      return;
    }

    try {
      setJiraTesting(true);
      const response = await fetch(getApiEndpoint("/api/jira/test-connection"), {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          api_url: jiraConfig.api_url,
          email: jiraConfig.email,
          api_token: jiraConfig.api_token,
        }),
      });

      const data = await response.json();
      if (response.ok && data.success) {
        toast({
          title: "Conexão estabelecida",
          description: `Conexão com Jira estabelecida com sucesso! ${data.projects_count || 0} projeto(s) encontrado(s).`,
        });
        fetchJiraProjects();
      } else {
        toast({
          title: "Erro na conexão",
          description: data.error || "Não foi possível conectar ao Jira",
          variant: "destructive",
        });
      }
    } catch (error: any) {
      toast({
        title: "Erro",
        description: error.message || "Erro ao testar conexão",
        variant: "destructive",
      });
    } finally {
      setJiraTesting(false);
    }
  };

  const fetchJiraProjects = async () => {
    if (!jiraConfig.api_url || !jiraConfig.email || !jiraConfig.api_token) {
      return;
    }

    try {
      setLoadingJiraProjects(true);
      const response = await fetch(
        `${getApiEndpoint("/api/jira/projects")}?api_url=${encodeURIComponent(jiraConfig.api_url)}&email=${encodeURIComponent(jiraConfig.email)}&api_token=${encodeURIComponent(jiraConfig.api_token)}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.ok) {
        const data = await response.json();
        setJiraProjects(data.projects || []);
      }
    } catch (error) {
      console.error("Erro ao buscar projetos:", error);
    } finally {
      setLoadingJiraProjects(false);
    }
  };

  const fetchJiraBoards = async () => {
    if (!jiraConfig.api_url || !jiraConfig.email || !jiraConfig.api_token) {
      return;
    }

    try {
      setLoadingJiraBoards(true);
      const response = await fetch(
        `${getApiEndpoint("/api/jira/boards")}?api_url=${encodeURIComponent(jiraConfig.api_url)}&email=${encodeURIComponent(jiraConfig.email)}&api_token=${encodeURIComponent(jiraConfig.api_token)}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.ok) {
        const data = await response.json();
        setJiraBoards(data.boards || []);
      }
    } catch (error) {
      console.error("Erro ao buscar boards:", error);
    } finally {
      setLoadingJiraBoards(false);
    }
  };

  const handleSaveJiraConfig = async () => {
    if (jiraConfig.enabled && (!jiraConfig.api_url || !jiraConfig.email || !jiraConfig.api_token || !jiraConfig.project_key)) {
      toast({
        title: "Campos obrigatórios",
        description: "Preencha todos os campos obrigatórios quando a integração estiver habilitada",
        variant: "destructive",
      });
      return;
    }

    try {
      setJiraSaving(true);
      const response = await fetch(getApiEndpoint("/api/jira/config"), {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          ...jiraConfig,
          config_type: "user" // Sempre salvar como configuração do usuário
        }),
      });

      if (response.ok) {
        toast({
          title: "Configuração salva",
          description: "Sua configuração do Jira foi salva! Quando você criar um chamado, um card será criado automaticamente no seu Jira.",
        });
        await fetchJiraConfig();
      } else {
        const data = await response.json();
        toast({
          title: "Erro",
          description: data.error || "Erro ao salvar configuração",
          variant: "destructive",
        });
      }
    } catch (error: any) {
      toast({
        title: "Erro",
        description: error.message || "Erro ao salvar configuração",
        variant: "destructive",
      });
    } finally {
      setJiraSaving(false);
    }
  };

  const handleSaveName = async () => {
    if (!token || !name.trim()) return;
    setSavingName(true);
    try {
      const res = await fetch(getApiEndpoint("/api/profile"), {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ name: name.trim() }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || "Erro ao salvar");
      }
      setProfile((p) => (p ? { ...p, name: name.trim() } : null));
      const userStr = getUser();
      if (userStr) {
        try {
          const user = JSON.parse(userStr);
          updateUser({ ...user, name: name.trim() });
        } catch (_) {}
      }
      toast({ title: t.profile.successName });
    } catch (e) {
      toast({ title: "Erro", description: (e as Error).message, variant: "destructive" });
    } finally {
      setSavingName(false);
    }
  };

  const handleAvatarClick = () => fileInputRef.current?.click();

  const handleAvatarChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !token) return;
    if (!file.type.startsWith("image/")) {
      toast({ title: "Erro", description: "Selecione uma imagem (JPEG, PNG, GIF, WEBP).", variant: "destructive" });
      return;
    }
    const url = URL.createObjectURL(file);
    setCropImageSrc(url);
    setCrop({ x: 0, y: 0 });
    setZoom(1);
    setCroppedAreaPixels(null);
    e.target.value = "";
  };

  const closeCropDialog = useCallback(() => {
    if (cropImageSrc) URL.revokeObjectURL(cropImageSrc);
    setCropImageSrc(null);
    setCroppedAreaPixels(null);
  }, [cropImageSrc]);

  const handleCropConfirm = async () => {
    if (!cropImageSrc || !croppedAreaPixels || !token) return;
    setUploadingAvatar(true);
    try {
      const blob = await getCroppedImg(cropImageSrc, croppedAreaPixels);
      const file = new File([blob], "avatar.jpg", { type: "image/jpeg" });
      closeCropDialog();
      const formData = new FormData();
      formData.append("avatar", file);
      const res = await fetch(getApiEndpoint("/api/profile/avatar"), {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || "Erro ao enviar foto");
      }
      const data = await res.json();
      setProfile((p) => (p ? { ...p, avatar_url: data.avatar_url } : null));
      const userStr = getUser();
      if (userStr) {
        try {
          const user = JSON.parse(userStr);
          updateUser({ ...user, avatar_url: data.avatar_url });
        } catch (_) {}
      }
      toast({ title: t.profile.successAvatar });
      window.dispatchEvent(new Event("userChanged"));
    } catch (e) {
      toast({ title: "Erro", description: (e as Error).message, variant: "destructive" });
    } finally {
      setUploadingAvatar(false);
    }
  };

  const handleSendCode = async () => {
    if (!token) return;
    setSendingCode(true);
    try {
      const res = await fetch(getApiEndpoint("/api/profile/request-change-password-code"), {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error("Erro ao enviar código");
      setCodeSent(true);
      toast({ title: t.profile.codeSent });
    } catch (e) {
      toast({ title: "Erro", description: "Não foi possível enviar o código.", variant: "destructive" });
    } finally {
      setSendingCode(false);
    }
  };

  const handleChangePassword = async () => {
    if (!token || !code.trim() || !newPassword || !confirmPassword) return;
    if (newPassword.length < 6) {
      toast({ title: t.profile.errorPasswordLength, variant: "destructive" });
      return;
    }
    if (newPassword !== confirmPassword) {
      toast({ title: t.profile.errorPasswordMatch, variant: "destructive" });
      return;
    }
    setChangingPassword(true);
    try {
      const res = await fetch(getApiEndpoint("/api/profile/change-password"), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ code: code.trim(), new_password: newPassword }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || "Erro ao alterar senha");
      toast({ title: t.profile.successPassword });
      setCode("");
      setNewPassword("");
      setConfirmPassword("");
      setCodeSent(false);
    } catch (e) {
      toast({ title: (e as Error).message === "Código inválido ou expirado" ? t.profile.errorCode : "Erro", description: (e as Error).message, variant: "destructive" });
    } finally {
      setChangingPassword(false);
    }
  };

  const avatarSrc = profile?.avatar_url
    ? (profile.avatar_url.startsWith("http") ? profile.avatar_url : getApiEndpoint(profile.avatar_url))
    : null;

  if (loading) {
    return (
      <>
        <Header />
        <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center p-4">
          <Loader2Icon className="h-8 w-8 animate-spin text-blue-600" />
        </div>
      </>
    );
  }

  if (!profile) {
    return (
      <>
        <Header />
        <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center p-4">
          <p className="text-gray-600 dark:text-gray-400">Perfil não encontrado.</p>
        </div>
      </>
    );
  }

  return (
    <>
      <Header />
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 py-8 px-4">
        <div className="max-w-xl mx-auto">
          <Card className="border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 shadow-sm">
            <CardHeader>
              <CardTitle className="text-gray-900 dark:text-gray-100">{t.profile.title}</CardTitle>
              <CardDescription className="text-gray-500 dark:text-gray-400">
                {t.profile.emailReadOnly}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-8">
              {/* Avatar */}
              <div className="flex flex-col items-center gap-4">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/jpeg,image/png,image/gif,image/webp"
                  className="hidden"
                  onChange={handleAvatarChange}
                />
                <div className="relative group">
                  <Avatar className="h-28 w-28 ring-4 ring-blue-100 dark:ring-gray-700 flex-shrink-0">
                    <AvatarImage src={avatarSrc || undefined} alt={profile.name} className="object-cover" />
                    <AvatarFallback className="bg-gradient-to-br from-blue-500 to-indigo-600 text-white text-2xl font-semibold">
                      {profile.name?.charAt(0).toUpperCase() || profile.email?.charAt(0).toUpperCase() || "U"}
                    </AvatarFallback>
                  </Avatar>
                  <button
                    type="button"
                    onClick={handleAvatarClick}
                    disabled={uploadingAvatar}
                    className="absolute bottom-0 right-0 rounded-full bg-blue-600 hover:bg-blue-700 text-white p-2 shadow-lg transition-opacity disabled:opacity-50"
                    aria-label={t.profile.avatarChange}
                  >
                    {uploadingAvatar ? (
                      <Loader2Icon className="h-5 w-5 animate-spin" />
                    ) : (
                      <CameraIcon className="h-5 w-5" />
                    )}
                  </button>
                </div>
                <p className="text-sm text-gray-500 dark:text-gray-400">{t.profile.avatar}</p>
              </div>

              {/* Dialog de recorte */}
              <Dialog open={!!cropImageSrc} onOpenChange={(open) => !open && closeCropDialog()}>
                <DialogContent className="max-w-lg bg-white dark:bg-gray-900 border-gray-200 dark:border-gray-800">
                  <DialogHeader>
                    <DialogTitle className="text-gray-900 dark:text-gray-100">{t.profile.avatarCrop}</DialogTitle>
                  </DialogHeader>
                  {cropImageSrc && (
                    <>
                      <p className="text-sm text-gray-500 dark:text-gray-400">{t.profile.avatarCropHint}</p>
                      <div className="relative h-[320px] w-full rounded-lg overflow-hidden bg-gray-100 dark:bg-gray-800">
                        <Cropper
                          image={cropImageSrc}
                          crop={crop}
                          zoom={zoom}
                          aspect={1}
                          onCropChange={setCrop}
                          onCropComplete={onCropComplete}
                          onZoomChange={setZoom}
                          style={{ containerStyle: { backgroundColor: "transparent" } }}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-gray-700 dark:text-gray-300 text-sm">Zoom</Label>
                        <Slider
                          value={[zoom]}
                          min={1}
                          max={3}
                          step={0.1}
                          onValueChange={([v]) => setZoom(v ?? 1)}
                          className="w-full"
                        />
                      </div>
                    </>
                  )}
                  <DialogFooter className="gap-2 sm:gap-0">
                    <Button variant="outline" onClick={closeCropDialog}>
                      {t.profile.avatarCropCancel}
                    </Button>
                    <Button onClick={handleCropConfirm} disabled={!croppedAreaPixels || uploadingAvatar}>
                      {uploadingAvatar ? <Loader2Icon className="h-4 w-4 animate-spin mr-2" /> : null}
                      {t.profile.avatarCropConfirm}
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>

              {/* Nome e e-mail */}
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="name" className="text-gray-700 dark:text-gray-300">{t.profile.name}</Label>
                  <Input
                    id="name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className="bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="email" className="text-gray-700 dark:text-gray-300">{t.profile.email}</Label>
                  <Input
                    id="email"
                    value={profile.email}
                    readOnly
                    disabled
                    className="bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-400 cursor-not-allowed"
                  />
                </div>
                <Button onClick={handleSaveName} disabled={savingName}>
                  {savingName ? <Loader2Icon className="h-4 w-4 animate-spin mr-2" /> : null}
                  {t.profile.saveName}
                </Button>
              </div>

              {/* Troca de senha */}
              <div className="border-t border-gray-200 dark:border-gray-700 pt-6 space-y-4">
                <h3 className="font-medium text-gray-900 dark:text-gray-100">{t.profile.changePassword}</h3>
                {!codeSent ? (
                  <Button variant="outline" onClick={handleSendCode} disabled={sendingCode}>
                    {sendingCode ? <Loader2Icon className="h-4 w-4 animate-spin mr-2" /> : null}
                    {t.profile.sendCode}
                  </Button>
                ) : (
                  <>
                    <div className="space-y-2">
                      <Label htmlFor="code" className="text-gray-700 dark:text-gray-300">{t.profile.code}</Label>
                      <Input
                        id="code"
                        value={code}
                        onChange={(e) => setCode(e.target.value)}
                        placeholder="000000"
                        maxLength={6}
                        className="bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="newPassword" className="text-gray-700 dark:text-gray-300">{t.profile.newPassword}</Label>
                      <Input
                        id="newPassword"
                        type="password"
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        className="bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="confirmPassword" className="text-gray-700 dark:text-gray-300">{t.profile.confirmPassword}</Label>
                      <Input
                        id="confirmPassword"
                        type="password"
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        className="bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700"
                      />
                    </div>
                    <Button onClick={handleChangePassword} disabled={changingPassword}>
                      {changingPassword ? <Loader2Icon className="h-4 w-4 animate-spin mr-2" /> : null}
                      {t.profile.changePasswordSubmit}
                    </Button>
                  </>
                )}
              </div>

              {/* Integração com Jira */}
              <div className="border-t border-gray-200 dark:border-gray-700 pt-6 space-y-4">
                <h3 className="font-medium text-gray-900 dark:text-gray-100">Integração Pessoal com Jira</h3>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Configure seu Jira pessoal. Esta configuração só será usada se sua empresa não tiver Jira configurado.
                </p>
                
                {jiraLoading ? (
                  <p className="text-sm text-gray-500 dark:text-gray-400">Carregando configuração...</p>
                ) : jiraConfig.enabled && jiraConfig.api_url ? (
                  <div className="rounded-lg border border-green-200 dark:border-green-800 bg-green-50 dark:bg-green-900/20 p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium text-green-900 dark:text-green-100">Jira conectado</p>
                        <p className="text-sm text-green-700 dark:text-green-300 mt-1">
                          {jiraConfig.api_url} • Projeto: {jiraConfig.project_key || "Não configurado"}
                        </p>
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={async () => {
                          const token = getToken();
                          if (!token) return;
                          await fetch(getApiEndpoint("/api/jira/config"), {
                            method: "POST",
                            headers: {
                              Authorization: `Bearer ${token}`,
                              "Content-Type": "application/json",
                            },
                            body: JSON.stringify({
                              enabled: false,
                              api_url: "",
                              email: "",
                              api_token: "",
                              project_key: "",
                              config_type: "user"
                            }),
                          });
                          await fetchJiraConfig();
                          toast({ title: "Jira desconectado" });
                        }}
                        className="border-red-300 text-red-700 hover:bg-red-50 dark:border-red-800 dark:text-red-400"
                      >
                        Desconectar
                      </Button>
                    </div>
                  </div>
                ) : (
                  <>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
                      Conecte sua conta Jira para criar cards automaticamente ao abrir chamados.
                    </p>
                    <Button
                      type="button"
                      variant="default"
                      className="w-full mb-4"
                      disabled={!token || jiraOAuthStarting}
                      onClick={async () => {
                        if (!token) return;
                        setJiraOAuthStarting(true);
                        try {
                          const res = await fetch(getApiEndpoint("/api/jira/oauth/start"), {
                            method: "POST",
                            headers: {
                              Authorization: `Bearer ${token}`,
                              "Content-Type": "application/json",
                            },
                            body: JSON.stringify({ config_type: "user" }),
                          });
                          const data = await res.json();
                          if (data.redirect_url) {
                            window.location.href = data.redirect_url;
                            return;
                          }
                          // Sem OAuth no servidor: abre a página do Jira para criar o token
                          window.open(ATLASSIAN_TOKEN_URL, "_blank", "noopener,noreferrer");
                          toast({
                            title: "Página do Jira aberta",
                            description: "Crie o token na aba que abriu, copie e cole no campo \"Token da API\" abaixo. Preencha também a URL do Jira e seu e-mail.",
                          });
                        } catch (e: unknown) {
                          const msg = e instanceof Error ? e.message : "Falha ao conectar com Jira";
                          toast({ title: "Erro", description: msg, variant: "destructive" });
                        } finally {
                          setJiraOAuthStarting(false);
                        }
                      }}
                    >
                      {jiraOAuthStarting ? <Loader2Icon className="h-4 w-4 animate-spin mr-2" /> : null}
                      {jiraOAuthStarting ? "Abrindo..." : "Entrar com Jira (abre a página para criar token)"}
                    </Button>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mb-3 text-center">ou preencha manualmente:</p>
                    <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
                      <p className="text-sm text-blue-900 dark:text-blue-100 font-medium mb-2">
                        Como obter o Token da API:
                      </p>
                      <ol className="text-xs text-blue-700 dark:text-blue-300 space-y-1 list-decimal list-inside">
                        <li>Acesse: <a href={ATLASSIAN_TOKEN_URL} target="_blank" rel="noopener noreferrer" className="underline">Página de tokens da Atlassian</a></li>
                        <li>Clique em "Create API token"</li>
                        <li>Dê um nome para o token e copie o valor gerado</li>
                        <li>Cole o token no campo abaixo</li>
                      </ol>
                    </div>

                    <div className="space-y-4">
                      <div className="space-y-2">
                        <Label htmlFor="simple-jira-url" className="text-gray-700 dark:text-gray-300">
                          URL do seu Jira *
                        </Label>
                        <Input
                          id="simple-jira-url"
                          type="url"
                          value={jiraConfig.api_url}
                          onChange={(e) => setJiraConfig({ ...jiraConfig, api_url: e.target.value })}
                          placeholder="https://seu-dominio.atlassian.net"
                          className="bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700"
                        />
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="simple-jira-email" className="text-gray-700 dark:text-gray-300">
                          Email da sua conta Jira *
                        </Label>
                        <Input
                          id="simple-jira-email"
                          type="email"
                          value={jiraConfig.email}
                          onChange={(e) => setJiraConfig({ ...jiraConfig, email: e.target.value })}
                          placeholder="seu-email@exemplo.com"
                          className="bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700"
                        />
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="simple-jira-token" className="text-gray-700 dark:text-gray-300">
                          Token da API *
                        </Label>
                        <Input
                          id="simple-jira-token"
                          type="password"
                          value={jiraConfig.api_token}
                          onChange={(e) => setJiraConfig({ ...jiraConfig, api_token: e.target.value })}
                          placeholder="Cole aqui o token que você copiou"
                          className="bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700"
                        />
                      </div>

                      <Button
                        onClick={async () => {
                          if (!jiraConfig.api_url || !jiraConfig.email || !jiraConfig.api_token) {
                            toast({
                              title: "Campos obrigatórios",
                              description: "Preencha todos os campos",
                              variant: "destructive",
                            });
                            return;
                          }

                          try {
                            setJiraSaving(true);
                            const token = getToken();
                            if (!token) return;

                            const response = await fetch(getApiEndpoint("/api/jira/connect"), {
                              method: "POST",
                              headers: {
                                Authorization: `Bearer ${token}`,
                                "Content-Type": "application/json",
                              },
                              body: JSON.stringify({
                                jira_url: jiraConfig.api_url,
                                email: jiraConfig.email,
                                api_token: jiraConfig.api_token,
                                config_type: "user"
                              }),
                            });

                            const data = await response.json();
                            if (response.ok && data.success) {
                              toast({
                                title: "Jira conectado!",
                                description: `Conectado com sucesso ao projeto ${data.config.project_name} (${data.config.project_key})`,
                              });
                              setJiraConfig({ ...jiraConfig, api_token: "", project_key: data.config.project_key });
                              await fetchJiraConfig();
                            } else {
                              toast({
                                title: "Erro ao conectar",
                                description: data.error || "Não foi possível conectar ao Jira",
                                variant: "destructive",
                              });
                            }
                          } catch (error: any) {
                            toast({
                              title: "Erro",
                              description: error.message || "Erro ao conectar com Jira",
                              variant: "destructive",
                            });
                          } finally {
                            setJiraSaving(false);
                          }
                        }}
                        disabled={jiraSaving || !jiraConfig.api_url || !jiraConfig.email || !jiraConfig.api_token}
                        className="w-full"
                      >
                        {jiraSaving ? <Loader2Icon className="h-4 w-4 animate-spin mr-2" /> : null}
                        {jiraSaving ? "Conectando..." : "Conectar com Jira"}
                      </Button>
                    </div>
                  </>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </>
  );
};

export default EditProfile;
